import { context, actions, beAxios as axios } from './backend';
import {
    formatFormStates,
    formatFormInitData,
    formatValueList,
    formatFormType,
    genericResponseCall,
} from './utils';
import oAuth from './oAuth';

let valuelist = [];

function toEntityList(data) {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.Data)) return data.Data;
    if (Array.isArray(data.data)) return data.data;
    return [data];
}

function hasRelatedEntityId(id) {
    return id !== undefined && id !== null && id !== '' && id !== -1 && id !== '-1';
}

function firstDefined(item, keys) {
    for (const key of keys) {
        if (item[key] !== undefined && item[key] !== null && item[key] !== '') {
            return item[key];
        }
    }
    return '';
}

function proxyGet(path, { params, headers } = {}) {
    const token = context.loginToken;
    if (!context.API_URL || !token) {
        return Promise.reject(new Error('Missing API_URL or token for proxy request'));
    }

    const search = params ? new URLSearchParams(params).toString() : '';
    const url = `${context.API_URL}${path}${search ? `?${search}` : ''}`;
    const xfmServer = context.xfmServer;
    const requestHeaders = {
        'x-fm-token': token,
        'x-fm-device-type': '101',
        'x-fm-version': context.webVersion || '4.dev-web4',
        'x-fm-db-server': xfmServer && xfmServer !== '-1' ? xfmServer : 'default',
        'Cache-Control': 'max-age=0, no-cache, must-revalidate, proxy-revalidate',
        'Content-Type': 'application/json;charset=UTF-8',
        ...headers,
    };

    return fetch(url, { method: 'GET', headers: requestHeaders }).then((res) => {
        if (!res.ok) {
            return Promise.reject(new Error(`Proxy request failed: ${res.status}`));
        }
        return res.json();
    });
}

let companyContactsCache = [];

function cacheContacts(items) {
    companyContactsCache = items || [];
    return companyContactsCache;
}

function findCachedContact(id) {
    return companyContactsCache.find((el) => String(el.id ?? el.Id) === String(id));
}

function fetchCompanyContacts(accountId) {
    return proxyGet('/contacts/sfm', {
        params: { companies: String(accountId) },
        headers: {
            'x-fm-entity-count': '99999',
            'x-fm-entity-offset': '0',
            'x-extra-field': '[]',
            'x-sort-field': '',
            'x-sort-dir': '-1',
            'x-selection-token': '',
        },
    }).then((data) => cacheContacts(data?.Success ? data.Data || [] : []));
}

function fetchContactById(id) {
    const cached = findCachedContact(id);
    if (cached) {
        return Promise.resolve([cached]);
    }
    return proxyGet(`/contacts/${id}`, {
        params: { withExtraFields: 'true' },
    }).then((data) => toEntityList(data));
}

function normalizeContacts(items) {
    return items.map((el) => ({
        ...el,
        id: el.id ?? el.Id,
        name: firstDefined(el, ['name', 'nombre', 'Name', 'firstName']),
        lastName: firstDefined(el, ['lastName', 'apellidos', 'Surnames', 'surnames']),
        email: firstDefined(el, [
            'email',
            'Email',
            'email1',
            'Email1',
            'EMail',
            'mail',
            'emailAddress',
            'EmailAddress',
        ]),
    }));
}

const listenersFunc = {
    getContext() {
        return context;
    },

    getToken({ guid }) {
        return localStorage.getItem(`fmFragmentToken-${guid}`);
    },

    getNewToken({ guid }) {
        return oAuth(guid, context.loginToken).then((res) => {
            localStorage.setItem(`fmFragmentToken-${guid}`, res);
            return res;
        });
    },

    getLiteral({ literal }) {
        let item = sessionStorage.getItem('fm_newLiterals');

        if (!item) return null;
        try {
            item = JSON.parse(item);
            if (typeof item !== 'object') return null;
            return item[literal];
        } catch (error) {
            console.error(error);
            return null;
        }
    },

    getFilteredUsers() {
        let item = sessionStorage.getItem('userFilter');

        if (!item) return null;
        try {
            item = JSON.parse(item);
            if (!Array.isArray(item)) return null;
            return item.join(', ');
        } catch (error) {
            console.error(error);
            return null;
        }
    },

    // FORMS //

    getFormInitData() {
        const getForm = (id) => {
            if (!id) {
                return Promise.resolve({ data: {} });
            }
            return axios.get(`/forms/v1/${id}`);
        };
        return new Promise((resolve, reject) => {
            getForm(context.form.id)
                .then(({ data }) => resolve(formatFormInitData(data)))
                .catch(reject);
        });
    },

    getFormStates() {
        return axios.get('/forms/v1/states').then(formatFormStates);
    },

    getValueList({ tableName }) {
        return new Promise((resolve, reject) => {
            if (valuelist[tableName]) {
                resolve(valuelist[tableName]);
            } else {
                function getValueListPage(page, list) {
                    return axios
                        .get(`/list/v1/${tableName}?page=${page}`, { headers: { count: 500 } })
                        .then((res) => {
                            if (res.data.length < 1) return list;
                            list.push(...res.data);
                            return getValueListPage(page + 1, list);
                        })
                        .then((res) => {
                            valuelist[tableName] = formatValueList(res);
                            resolve(valuelist[tableName]);
                        })
                        .catch(reject);
                }
                getValueListPage(0, []);
            }
        });
    },

    getFormType({ idTipoForm }) {
        const result = devData['getFilteredForms'][idTipoForm] || {};
        return Promise.resolve(formatFormType(result));
    },

    getRelatedEntity({ id, fromEntity, getEntity }) {
        const parent = fromEntity || getEntity;
        const fromAccount = parent === 'accountId' || parent === 'accounts';

        if (getEntity === 'contacts' && fromAccount && hasRelatedEntityId(id)) {
            return fetchCompanyContacts(id).then(normalizeContacts);
        }

        if (getEntity === 'contacts' && hasRelatedEntityId(id)) {
            return fetchContactById(id).then(normalizeContacts);
        }

        return Promise.reject(
            new Error(`Unsupported getRelatedEntity: ${getEntity} / ${fromEntity}`),
        );
    },

    getUsers() {
        return listenersFunc.getRelatedEntity({ getEntity: 'users', fromEntity: 'users', id: -1 });
    },

    finishActivity() {
        if (!actions) return Promise.reject('No actions setted');
        actions.finishActivity();
        return Promise.resolve();
    },

    setTitle({ title }) {
        if (!actions) return Promise.reject('No actions setted');
        actions.setTitle(title);
        return Promise.resolve();
    },

    saveData({ formData }) {
        if (!actions) return Promise.reject('No actions setted');
        if (context.form.id) {
            actions.saveData();
            return axios.put(`/forms/v1/${context.form.id}`, formData).then(({ data }) => data);
        }
        return axios.post(`/accounts/v1/${context.entity.id}/forms`, formData).then(({ data }) => {
            const formId = data?.id ?? data?.Id;
            if (formId) {
                context.form = { ...context.form, id: formId };
                actions.saveData(formId);
            }
            return data;
        });
    },

    openDialogPicker() {
        return Promise.resolve();
    },

    openSignatureView() {
        if (!actions) return Promise.reject('No actions setted');
        const func = () => {
            actions.openSignatureView();
        };
        return genericResponseCall('openSignatureView', false, func);
    },

    showCameraImages() {
        if (!actions) return Promise.reject('No actions setted');
        actions.showCameraImages();
        return Promise.resolve();
    },

    hideCameraImages() {
        if (!actions) return Promise.reject('No actions setted');
        actions.hideCameraImages();
        return Promise.resolve();
    },

    expandImagesView() {
        if (!actions) return Promise.reject('No actions setted');
        actions.expandImagesView();
        return Promise.resolve();
    },

    collapseImagesView() {
        if (!actions) return Promise.reject('No actions setted');
        actions.collapseImagesView();
        return Promise.resolve();
    },
    showLoading() {
        if (!actions) return Promise.reject('No actions setted');
        actions.showLoading();
        return Promise.resolve();
    },

    hideLoading() {
        if (!actions) return Promise.reject('No actions setted');
        actions.hideLoading();
        return Promise.resolve();
    },

    showAlertDialog({ message, btnOk }) {
        if (!actions) return Promise.reject('No actions setted');
        const func = () => {
            actions.showAlertDialog({
                type: 'alert',
                size: 'small',
                // title: t('title_warning'),
                text: message,
                confirmText: btnOk,
                // cancelText: t('cfm_action_no'),
                // data: { id },
            });
        };
        return genericResponseCall('showAlertDialog', false, func);
    },

    showConfirmDialog({ message, btnOkStr, btnKOStr }) {
        if (!actions) return Promise.reject('No actions setted');
        const func = () => {
            actions.showConfirmDialog({
                type: 'confirm',
                size: 'small',
                // title: t('title_warning'),
                text: message,
                confirmText: btnOkStr,
                cancelText: btnKOStr,
                // data: { id },
            });
        };
        return genericResponseCall('showConfirmDialog', false, func);
    },
};

export default listenersFunc;
